import { useMutation, useQuery } from '@tanstack/react-query';
import fetcher, { FleetGetListParams, FleetPaginated } from '../fetcher';
import { MasterData } from './master-data';
import { MPPDetail } from './mpp';
import { FileData, FilePayload } from './types';

export const usePrintVerificationTodoQuery = () => {
	return useQuery({
		queryKey: ['/disbursement-document-verification/get-things-todo'] as const,
		queryFn: ({ queryKey }) => {
			const [api] = queryKey;
			return fetcher<number>({
				api,
			});
		},
	});
};

export const usePrintVerificationSearchOptionQuery = () => {
	return useQuery({
		queryKey: [
			'/disbursement-document-verification/mpp-search-options',
		] as const,
		queryFn: ({ queryKey }) => {
			const [api] = queryKey;
			return fetcher<{
				searchByOptions: MasterData[];
				divisionOptions: MasterData[];
				frmOptions: MasterData[];
				fbdhOptions: MasterData[];
			}>({
				api,
			});
		},
	});
};

export type PrintVerificationSearchMPPParam = FleetGetListParams &
	Partial<{
		divisionId: number;
		frmId: number;
		fbdhId: number;
	}>;

export const usePrintVerificationSearchMPP = (
	body: PrintVerificationSearchMPPParam,
) => {
	return useQuery({
		queryKey: ['/disbursement-document-verification/search-mpp', body] as const,
		queryFn: ({ queryKey }) => {
			const [api] = queryKey;
			return fetcher<
				FleetPaginated<{
					id: number;
					no: number;
					mppId: number;
					mppNo: string;
					customerId: number;
					customerName: string;
					bmppGroupId: number;
					bmppGroupName: string;
					frm: string;
					fbdh: string;
					division: string;
					allowAction: boolean;
				}>
			>({
				api,
				body,
			});
		},
	});
};

export const usePrintVerificationSearchPOOptionQuery = () => {
	return useQuery({
		queryKey: [
			'/disbursement-document-verification/po-search-options',
		] as const,
		queryFn: ({ queryKey }) => {
			const [api] = queryKey;
			return fetcher<{
				searchByOptions: MasterData[];
				purchaseOrderStatusOptions: MasterData[];
				fcohStatusOptions: MasterData[];
			}>({
				api,
			});
		},
	});
};

export type PrintVerificationSearchPOOrderParam = FleetGetListParams & {
	mppId: number;
	purchaseOrderStatus?: string;
	applicationStatus?: string;
	fcohVerificationStatus?: string;
	purchaseOrderDate?: string;
};

export const usePrintVerificationSearchPOOrderQuery = (
	body: PrintVerificationSearchPOOrderParam,
) => {
	return useQuery({
		queryKey: [
			'/disbursement-document-verification/search-purchase-order',
			body,
		] as const,
		queryFn: ({ queryKey }) => {
			const [api] = queryKey;
			return fetcher<{
				mainInformation: {
					customerId: string;
					customerName: string;
					bmppGroupId: string;
					bmppGroupName: string;
					mppId: number;
					mppNo: string;
				};
				items: {
					simulationId: number;
					simulationNo: string;
					disbursementId: number;
					disbursementDate: string;
					disbursementDocumentNo: string;
					purchaseOrderId: number;
					purchaseOrderNo: string;
					financingProduct: MasterData;
					applicationId: string;
					agreementId: number;
					agreementNo: string;
					plakatNo: string;
					supplierName: string;
					supplierPicName: string;
					supplierPhone: string;
					asset: string;
					unit: number;
					ntf: number;
					purchaseOrderDate: string;
					purchaseOrderStatus: string;
					verificationStatus: string;
					applicationStatus: string;
					allowCheck: boolean;
				}[];
			}>({
				api,
				body,
			});
		},
	});
};

export const usePrintVerificationDetailQuery = (body: {
	mppId: number;
	disbursementId?: number;
	financingProductId: number;
	purchaseOrderIds?: number[];
	applicationIds?: string[];
}) => {
	return useQuery({
		queryKey: [
			'/disbursement-document-verification/get-verification-detail',
			body,
		] as const,
		queryFn: ({ queryKey }) => {
			const [api] = queryKey;
			return fetcher<
				{
					id: number;
					disbursementNo: string;
					status: number;
					mppDetails: MPPDetail;
					fcdvsList: MasterData[];
					approvalHistory: {
						id: number;
						name: string;
						date: string;
						process: string;
						status: string;
						notes: string;
					}[];
					realizationDocuments: {
						order: number;
						documentTypeName: string;
						documents: {
							id: number;
							no: number;
							name: string;
							slug: string;
							file: FileData;
						}[];
					}[];
					fcasName: string;
					fcdvsName: string;
					returnUsers: { id: number; value: string }[];
					financingProduct: MasterData;
					agreementInformation: {
						signContractOption: string;
						agreementNo: string;
						agreementDate: string;
						agreementDocument: FileData;
						directorStatementDocument: FileData;
						commissionerStatementDocument: FileData;
					};
					billingInformations: {
						simulationNo: string;
						simulationId: number;
						purchaseOrderNo: string;
						purchaseOrderId: number;
						dealerName: string;
						unitCount: number;
						contactPerson: string;
						supplierPhoneNo: string;
						accountInformation: string;
						estarAgreementDetails: {
							estarAgreementNo: string;
							assetName: string;
							deliveryDate: string;
							buyBackGuaranteeValidity: string;
							receiverName: string;
							receiverLocation: string;
							chasisNo: string;
							engineNo: string;
							manufacturingYear: string;
							color: string;
							licensePlate: string;
							picName: string;
							masterAddress: string;
							lastYearVehicleTax: string;
							bpkbName: string;
							bpkbAddress: string;
						}[];
						billingDocumentDetails: {
							estarAgreementNo: string;
							assetName: string;
							billingUploadDocuments: {
								masterDocumentId: number;
								name: string;
								document: FileData;
								allowUpload: boolean;
							}[];
							unitSequence: number;
						}[];
						disbursementRequirementDocumentDetails: {
							estarAgreementNo: string;
							assetName: string;
							disbursementUploadDocuments: {
								masterDocumentId: number;
								name: string;
								document: FileData;
								allowUpload: boolean;
								unitSequence: number;
							}[];
							unitSequence: number;
						}[];
					}[];
				} & Record<
					| 'realizationDocumentList'
					| 'specialConditionDocumentList'
					| 'goliveConditionDocumentList',
					{
						documents: {
							id: number;
							name: string;
							file: FileData;
						}[];
					}
				>
			>({
				api,
				body,
			});
		},
	});
};

export type PrintVerificationRequestPayload = {
	disbursementId: number;
	applicationIds?: string[];
	disbursementBillingDocuments?: {
		purchaseOrderId: number;
		billingDocuments: {
			unitSeq: number;
			masterDisbursementDocumentId: number;
			document: FilePayload;
		}[];
		disbursementDocuments: {
			unitSeq: number;
			masterDisbursementDocumentId: number;
			document: FilePayload;
		}[];
	}[];
	notes: string;
} & Record<
	| 'disbursementRealizationDocuments'
	| 'disbursementSpecialConditionDocuments'
	| 'disbursementGoliveConditionDocuments',
	{
		// unitSeq: number;
		masterDisbursementDocumentId: number;
		document: FilePayload;
		name: string;
	}[]
>;

export const usePrintVerificationRequestMutation = () => {
	return useMutation({
		mutationFn: (body: PrintVerificationRequestPayload) => {
			return fetcher<boolean>({
				api: '/disbursement-document-verification/request-verification',
				body,
			});
		},
	});
};

export type PrintVerificationApprovalPayload = Pick<
	PrintVerificationRequestPayload,
	'disbursementBillingDocuments'
> & {
	disbursementId: number;
	action: string;
	notes: string;
	returnUser?: number;
	covenants: {
		id: number;
		statusId: string;
		additionalInformation: string;
	}[];
};
export const usePrintVerificationApprovalMutation = () => {
	return useMutation({
		mutationFn: (body: PrintVerificationApprovalPayload) => {
			return fetcher<boolean>({
				api: '/disbursement-document-verification/approve-disbursement',
				body,
			});
		},
	});
};

export const useViewDocumentTemplateMutation = () => {
	return useMutation({
		mutationFn: (body: { financingProductId: number; mppId: number }) => {
			return fetcher<{ signedUrl: string }[]>({
				api: '/disbursement-document-verification/view-document-template',
				body,
			});
		},
	});
};
